import type React from 'react';
import { Chip, Separator, Typography } from '@heroui/react';
import { Navbar } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import { ModeBadge } from '@/components/modeBadge';
import { DashboardLanguageToggle } from '@/layout/components/DashboardLanguageToggle';
import { DashboardThemeToggle } from '@/layout/components/DashboardThemeToggle';
import { LOCK_TILL_SHORTCUT, useShortcut } from '@/components/shortcuts';
import { ShiftActionButton } from '../../shift/ShiftActionButton';
import { SellerTopBarActions } from './SellerTopBarActions';
import { SellerIdentityMenu } from './SellerIdentityMenu';
import { useClock } from './useClock';
import { useSellerSession } from './useSellerSession';

/**
 * POS top bar, built on the Pro `Navbar`: brand, enrolled branch, the shift
 * action, a live clock, the operations (`SellerTopBarActions`, CTA stubs
 * until their modals land), the language and theme toggles, and the seller
 * identity menu that locks the till (switch seller). `Ctrl+Shift+L` locks the
 * till from anywhere on the sale screen; its hint sits next to the menu entry.
 */
export const SellerTopBar: React.FC = () => {
    const clock = useClock();
    const { sellerName, sellerInitials, branchName, lock } = useSellerSession();

    useShortcut(LOCK_TILL_SHORTCUT.id, lock);

    return (
        <Navbar
            className="flex-shrink-0 border-b border-border bg-surface"
            {...getNodeTestIdProp('seller-topbar')}
            maxWidth="full"
        >
            <Navbar.Header className="gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="flex size-8 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                        <Icon
                            className="size-4"
                            icon="solar:cart-large-2-bold-duotone"
                        />
                    </div>
                    <Typography type="body-sm" weight="semibold">
                        BOS Seller
                    </Typography>
                </div>

                <Chip
                    color="accent"
                    {...getNodeTestIdProp('seller-branch-badge')}
                    variant="soft"
                >
                    <Icon
                        className="size-3.5"
                        icon="solar:shop-2-bold-duotone"
                    />
                    {branchName}
                </Chip>

                <ShiftActionButton />

                <Navbar.Spacer />

                {/* The run mode and API of a development build, beside the clock
                    where a tester at the till sees it; nothing in production
                    (SEL-66). */}
                <ModeBadge />

                <Typography
                    className="tabular-nums"
                    color="muted"
                    {...getNodeTestIdProp('seller-clock')}
                    type="body-sm"
                >
                    {clock}
                </Typography>

                <Separator className="h-6" orientation="vertical" />

                <SellerTopBarActions />

                <Separator className="h-6" orientation="vertical" />

                <div className="flex items-center gap-2">
                    <DashboardLanguageToggle
                        className="gap-0"
                        id="seller-topbar-language-toggle"
                        itemIdPrefix="seller-topbar"
                    />
                    <DashboardThemeToggle
                        className="gap-0"
                        id="seller-topbar-theme-toggle"
                        itemIdPrefix="seller-topbar"
                    />
                </div>

                <Separator className="h-6" orientation="vertical" />

                <SellerIdentityMenu
                    onLock={lock}
                    sellerInitials={sellerInitials}
                    sellerName={sellerName}
                />
            </Navbar.Header>
        </Navbar>
    );
};
